import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import {
  CustomTable,
  ErrorModal,
  Popover,
  SearchBar,
  Spinner,
  Status,
} from '../../components/shared';
import {
  userColumnData,
  userTableColumnExtensions,
} from '../../utils/mockUpData';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { allUsersAPI } from '../../redux/features/users/userSlice';
import { capitalizeFirstLetter, formatDate } from '../../utils/validator';
import { useSearchParams } from 'react-router-dom';
import {
  accountStatus,
  TableActions,
} from '../../components/shared/StatusUtility';

import { Children } from './utils';
import useOnClickOutside from '../../utils/hooks/useOnClickOutside';
import Filters from '../../components/shared/Filters';

const Users = () => {
  const { loading, error, allUsers } = useAppSelector(
    (state) => state.allUsers
  );
  const dispatch = useAppDispatch();

  // States
  const [selectedIds, setSelectedIds] = useState([]);
  const [activePopover, setActivePopover] = useState<number | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [searchName, setSearchName] = useSearchParams({
    search: '',
  });
  const [filters, setFilters] = useState({
    Users: [
      {
        title: 'Pro',
        value: false,
      },
      {
        title: 'Customer',
        value: false,
      },
    ],
  });
  // Get Query Pararms Value
  const searchedValue = searchName.get('search');

  const rowData = allUsers?.data?.map((item: any, index: number) => {
    const profile = item?.profile;
    return {
      index: index + 1,
      id: item?._id,
      name:
        profile !== '' && profile !== null && profile !== undefined
          ? capitalizeFirstLetter(`${profile?.firstName} ${profile?.lastName}`)
          : 'N/A',
      role: item?.role ? capitalizeFirstLetter(item?.role?.role) : '',
      phoneNumber: item?.phoneNumber ? item?.phoneNumber : '',
      email: item?.email ? item?.email : '',
      status: item?.isSuspended,
      isEmailVerified: item?.isEmailVerified ? item?.isEmailVerified : '',
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'MM/DD/YY, hh:mm A')
        : '',
      profile: item?.profile ? item?.profile : null,
    };
  });
  // Toggle Popover
  const handleToggle = (index: number) => {
    setActivePopover((prev) => (prev === index ? null : index));
  };

  // Data Providers
  const dataProviders = [
    {
      columnName: ['isEmailVerified'],
      func: (restProps: any) =>
        Status(restProps?.row?.isEmailVerified, 'Verified', 'Verified'),
    },
    {
      columnName: ['status'],
      func: (restProps: any) =>
        accountStatus(restProps?.row?.status, 'Suspended', 'Active'),
    },
    {
      columnName: ['action'],
      func: (restProps: any) => (
        <TableActions
          children={() => Children(restProps, handleCallUserList)}
          restProps={restProps}
          isActive={activePopover === restProps?.row?.index}
          onToggle={handleToggle}
          height='max-content'
          ref={popoverRef}
        />
      ),
    },
  ];

  // Handle Filters
  const onChangeFilters = (title: string, item: any, index: number) => {
    if (title === 'clearAll') {
      setFilters({
        Users: filters.Users.map((b) => ({ ...b, value: false })),
      });
      handleCallUserList();
      setIsFiltersOpen(false);
      return;
    }
    if (title === 'Users') {
      setFilters((prevFilters) => {
        const users = prevFilters.Users.map((b, i) => ({
          ...b,
          value: i === index ? !item?.value : false,
        }));
        return { Users: users };
      });
      const pro = index === 0 && !item?.value ? 'pro' : '';
      const customer = index === 1 && !item?.value ? 'customer' : '';
      const type = pro || customer;
      dispatch(
        allUsersAPI({
          type: type,
        })
      );
    }
  };

  // InlineSearch Fn
  const searchUsers = () => {
    if (!searchedValue) {
      handleCallUserList();
    }
    dispatch(allUsersAPI({ search: searchedValue }));
  };

  // Handle Call User List
  const handleCallUserList = () => {
    dispatch(allUsersAPI({}));
    setActivePopover(null);
  };

  // UseEffect
  useEffect(() => {
    handleCallUserList();
    // eslint-disable-next-line
  }, [dispatch]);

  //useOutSideClick
  useOnClickOutside(popoverRef, () => setActivePopover(null));

  //If Loading
  if (loading) {
    return <Spinner />;
  }

  return (
    <React.Fragment>
      <div className='w-full h-full flex flex-col gap-2 p-3'>
        <div className='flex gap-2'>
          <SearchBar
            value={searchedValue || ''}
            onChange={(e) => {
              e.target.value =
                e.target.value.charAt(0) === ' '
                  ? ''
                  : e.target.value.replace(/ +$/, ' ');
              if (e.target.value === '') {
                setSearchName();
                handleCallUserList();
              } else {
                setSearchName({
                  search: e.target.value,
                });
              }
            }}
            onClear={() => {
              setSearchName();
              handleCallUserList();
            }}
            onSearch={searchUsers}
          />
          <div>
            <Popover
              menuHeading={'Filters'}
              dropdownOpen={isFiltersOpen}
              setDropdownOpen={setIsFiltersOpen}
              active={filters.Users[0].value || filters.Users[1].value || false}
            >
              <Filters options={filters} onChange={onChangeFilters} />
            </Popover>
          </div>
        </div>
        <ErrorModal showModal={Boolean(error)} errorMsg={error} />
        {/* Table */}
        <div className=''>
          <CustomTable
            rows={rowData?.length > 0 ? rowData : []}
            columns={userColumnData}
            tableColumnExtensions={userTableColumnExtensions}
            dataProviders={dataProviders}
            selectedIds={selectedIds}
            setSelectedIds={(selectedIds: SetStateAction<never[]>) =>
              setSelectedIds(selectedIds)
            }
            componentHeader={true}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default Users;
